
import { useState, useEffect } from "react";
import { format, parse } from "date-fns";
import { CommunityEvent } from "@/types/rider";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { getAssetPath } from "@/utils/assetUtils";
import { motion } from "framer-motion";

interface EventCardProps {
  event: CommunityEvent;
  isSelected: boolean;
  onClick: () => void;
}

export const EventCard = ({ event, isSelected, onClick }: EventCardProps) => {
  const [imageError, setImageError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Format the event date if available
  const formatEventDate = (dateString?: string) => {
    if (!dateString) return "TBA";
    // For Breakaway events, the date is already in the correct format
    if (dateString.includes("Friday") || dateString.includes("Saturday")) {
      return dateString; // Return as-is for manually formatted dates
    }
    try {
      // If we receive a date-only string (yyyy-MM-dd), parse in local time to avoid UTC shift
      const isISODateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
      const date = isISODateOnly 
        ? parse(dateString, "yyyy-MM-dd", new Date()) 
        : new Date(dateString);
      return format(date, "EEEE, MMM dd"); // e.g., "Friday, Oct 03"
    } catch {
      return dateString; // fallback to original string if parsing fails
    }
  };
  
  // Get the appropriate image path based on event name
  const getEventImagePath = (eventName: string) => {
    // Map event names to their corresponding image files
    const eventImageMap: Record<string, string> = {
      "Coachella": "RiderEvent_Coachella_Test_Image.png",
      "EDC": "RiderEvent_EDC_Test_Image.png",
      "Beyond Wonderland": "RiderEvent_Beyond_Wonderland_Test_Image.png",
      "Los Angeles Lakers": "RiderEvent_Lakers_Test_Image.png",
      "Rufus Du Sol": "RiderEvent_Rufus_Du_Sol_Test_Image.png"
    };
    
    // Find a matching image based on event name (partial match)
    const matchingEvent = Object.keys(eventImageMap).find(key => 
      eventName.toLowerCase().includes(key.toLowerCase())
    );
    
    return matchingEvent ? eventImageMap[matchingEvent] : null;
  };
  
  const imagePath = getEventImagePath(event.name);
  
  // Debug logging to see what image we're receiving
  useEffect(() => {
    console.log("EventCard - Event name:", event.name);
    console.log("EventCard - event.image value:", event.image);
    console.log("EventCard - imagePath fallback:", imagePath);
    console.log("EventCard - Final image URL:", event.image || (imagePath ? getAssetPath(imagePath) : ''));
  }, [event.image, event.name, imagePath]);

  useEffect(() => {
    // Simulate loading delay for demo purposes
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <Card 
      className={cn(
        "relative w-full h-[450px] overflow-hidden rounded-2xl transition-all duration-300 shadow-lg",
        isSelected ? "ring-4 ring-neon-green" : ""
      )}
      onClick={onClick}
    >
      <div className="absolute inset-0 bg-black/25 z-10" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-70 z-20" />
      
      {!imageError && (event.image || imagePath) ? (
        <div className="absolute inset-0">
          <img 
            src={
              event.image 
                ? (event.image.startsWith('http') ? event.image : getAssetPath(event.image.replace(/^\//, '')))
                : (imagePath ? getAssetPath(imagePath) : '')
            }
            alt={event.name}
            className={cn(
              "w-full h-full object-cover transition-all duration-500",
              isLoaded ? "opacity-100" : "opacity-0",
            )}
            onLoad={() => setIsLoaded(true)}
            onError={(e) => {
              console.error(`Failed to load image for ${event.name}`, e);
              setImageError(true);
            }}
          />
        </div>
      ) : (
        <div className="bg-black w-full h-full" />
      )}
      
      {/* Top Left - Event Name and Date */}
      <div className="absolute top-6 left-6 z-30">
        <h3 className="font-bold text-2xl text-white mb-1">{event.name}</h3>
        <p className="text-xs text-black bg-white px-3 py-1 rounded-full inline-block">{formatEventDate(event.date)}</p>
      </div>
      
      {/* Bottom Left - Trip Type and Location */}
      <div className="absolute bottom-6 left-6 z-30">
        <p className="text-sm text-black bg-white px-3 py-1 rounded-full mb-2 inline-block">{event.startTime || "One Way"}</p>
        <p className="text-lg text-white/90">{event.location}</p>
      </div>
      
      {isSelected && (
        <div className="absolute top-4 right-4 bg-neon-green text-black rounded-full p-2 z-40">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
    </Card>
  );
};
